// Copy of components/transactionSearchResults/TransactionSearchResults.state.js on its own Redux slice for the Payments - Beta tab.
import * as S from "sanctuary";
import { createSelector } from "reselect";

import { openPayment } from "@/components/payment/Payment.state";
import {
  getTransactionSearchRequestPending,
  getTransactionDateFilter,
  getTransactionsSearchRequestError,
} from "@/components/transactionSearchPageV2Beta/TransactionSearchPageV2Beta.state";
import { getSearchPendingText } from "@/components/transactionSearchPageV2Beta/TransactionSearchPageV2Beta.state";
import { getUserTimezone } from "@/components/currentUser/CurrentUser.state";
import {
  RESULTS_PER_PAGE,
  PAYMENT_INITIATOR_SCHEDULE_SERVICE,
} from "@/components/transactionSearchResults/TransactionSearchResults.state";

// Shared with the classic slice so the two never drift.
export { RESULTS_PER_PAGE, PAYMENT_INITIATOR_SCHEDULE_SERVICE };

// `searchId` names the search these rows came back from, so a response that
// lost the race cannot land on a newer search's rows.
// `clientPaginated` marks these rows as the whole set, paged here.
export const SET_TRANSACTION_SEARCH_RESULTS =
  "transactionSearchResultsV2Beta/SET_TRANSACTION_SEARCH_RESULTS";
export const setTransactionSearchResults = ({
  transactions,
  resultsCount,
  page,
  searchId = null,
  clientPaginated = false,
}) => ({
  type: SET_TRANSACTION_SEARCH_RESULTS,
  payload: {
    transactions,
    resultsCount,
    page,
    searchId,
    clientPaginated,
  },
});

// A starting search empties the slice and records itself as the search it is
// waiting on. A caller that only wants it emptied names no search.
export const CLEAR_STATE = "transactionSearchResultsV2Beta/CLEAR_STATE";
export const clearState = ({ searchId = null } = {}) => ({
  type: CLEAR_STATE,
  payload: { searchId },
});

export const SET_CURRENT_PAGE = "transactionSearchResultsV2Beta/SET_CURRENT_PAGE";
export const setCurrentPage = ({ pageNumber }) => ({
  type: SET_CURRENT_PAGE,
  payload: { pageNumber },
});

export const PAGE_NEXT = "transactionSearchResultsV2Beta/PAGE_NEXT";
export const pageNext = () => ({
  type: PAGE_NEXT,
});

export const PAGE_PREVIOUS = "transactionSearchResultsV2Beta/PAGE_PREVIOUS";
export const pagePrevious = () => ({
  type: PAGE_PREVIOUS,
});

const INITIAL_STATE = {
  transactions: S.Nothing,
  totalSearchResultsCount: 0,
  searchResultsPageCount: 1,
  currentPage: 1,
  // The search this slice is waiting on, or null when nothing named one.
  activeSearchId: null,
  clientPaginated: false,
};

/**
 * isStaleSearchResult
 *
 * True when the response belongs to a search the slice has moved past. Needs
 * both sides tagged, so an untagged response is applied as usual.
 */
const isStaleSearchResult = (state, payload) =>
  !!state.activeSearchId &&
  !!payload?.searchId &&
  payload.searchId !== state.activeSearchId;

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_STATE:
      return { ...INITIAL_STATE, activeSearchId: payload?.searchId ?? null };
    case SET_TRANSACTION_SEARCH_RESULTS:
      // The lifecycle already ignores a superseded search's settlement; its
      // rows have to be dropped here too.
      if (isStaleSearchResult(state, payload)) return state;
      return {
        ...state,
        transactions: S.Just(payload.transactions),
        totalSearchResultsCount: payload.resultsCount,
        searchResultsPageCount: Math.max(
          1,
          Math.ceil(payload.resultsCount / RESULTS_PER_PAGE)
        ),
        currentPage: payload.page,
        clientPaginated: !!payload.clientPaginated,
      };
    case PAGE_NEXT:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(state.currentPage + 1),
      };
    case PAGE_PREVIOUS:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(state.currentPage - 1),
      };
    case SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(payload.pageNumber),
      };
    default:
      return state;
  }
};

const getState = (state) => state.transactionSearchResultsV2Beta;
export const getCurrentPage = (state) => getState(state).currentPage;
export const getCurrentPageZeroIndexed = (state) => getCurrentPage(state) - 1;
export const getIsClientPaginated = (state) => getState(state).clientPaginated;
export const getActiveSearchId = (state) => getState(state).activeSearchId;

export const getVisibleTransactions = createSelector(
  (state) => getState(state).transactions,
  getIsClientPaginated,
  getCurrentPage,
  (transactions, clientPaginated, currentPage) => {
    if (!clientPaginated || S.isNothing(transactions)) return transactions;
    const start = (currentPage - 1) * RESULTS_PER_PAGE;
    return S.Just(transactions.value.slice(start, start + RESULTS_PER_PAGE));
  }
);

export const mapDispatchToProps = {
  setCurrentPage,
  pageNext,
  pagePrevious,
  openTransactionDetails: (transactionId) => openPayment({ transactionId }),
};

export const mapStateToProps = (state) => ({
  ...getState(state),
  transactions: getVisibleTransactions(state),
  dateFilter: getTransactionDateFilter(state),
  searchRequestPending: getTransactionSearchRequestPending(state),
  searchRequestError: getTransactionsSearchRequestError(state),
  userTimezone: getUserTimezone(state),
  searchPendingText: getSearchPendingText(state),
});

export default reducer;
